import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { CloseRounded } from "@mui/icons-material";
import { toast } from "react-toastify";
import { ModalBox } from "../Outer/Modal";
import {
  CreateAuctionPassSession,
  GetAuctionPassOptions,
  RedeemAuctionPassCredit,
} from "../../redux/auth-reducer/auth-service";
import { CHECKOUT_OUTCOME, startCheckout } from "../../utils/helper/checkout";
import { readApiMessage } from "../../utils/helper/helper";

/**
 * Single-auction access, for the grid row the player chose to unlock (FE-16: moved out of
 * AuctionPlayer). Everything shown here comes back from the server: whether the auction can be
 * unlocked, whether a credit applies instead, and the sentence explaining either. The grid flags
 * only decide which button opens it.
 *
 * @param {object} props
 * @param {object|null} props.row The grid row being unlocked; null while the dialog is closed.
 * @param {() => void} props.onClose Closes the dialog.
 * @param {() => void} props.onRedeemed Called after a credit was applied: SignalR pushes bid state,
 *   not entitlement, so the row's flags only change on a refetch.
 */
export const AuctionPassDialog = ({ row, onClose, onRedeemed }) => {
  // `options` is whatever the server last said about this auction; the screen never decides for
  // itself what is on offer or how to word it.
  const [options, setOptions] = useState(null);
  const [busy, setBusy] = useState(false);
  /** {message, sessionUrl} - why the payment page was not reached, shown inside the dialog. */
  const [error, setError] = useState(null);

  const onCloseRef = useRef(onClose);
  useLayoutEffect(() => {
    onCloseRef.current = onClose;
  });

  // Each row opened starts from nothing; a reply for a row closed meanwhile is dropped.
  useEffect(() => {
    setOptions(null);
    setError(null);
    if (!row) {
      setBusy(false);
      return undefined;
    }

    let current = true;
    setBusy(true);
    GetAuctionPassOptions(row.auctionId).then((res) => {
      if (!current) return;
      setBusy(false);

      if (res?.data?.status === false) {
        toast.error(res?.data?.message);
        onCloseRef.current();
        return;
      }

      setOptions({
        message: res?.data?.message,
        ...(res?.data?.data?.[0] || {}),
      });
    });

    return () => {
      current = false;
    };
  }, [row]);

  const handlePurchase = async () => {
    setError(null);
    setBusy(true);

    // #238 AC8: a confirmed payment comes back to this page (the Auctions list or Auction Details),
    // where the auction then reads as unlocked.
    const result = await startCheckout(() => CreateAuctionPassSession(row?.auctionId), { returnOnSuccess: true });

    if (result.outcome === CHECKOUT_OUTCOME.REDIRECTING) {
      // The dialog stays open and busy while this tab leaves for Stripe, with the link behind
      // it in case the navigation does not happen.
      setError({ message: "", sessionUrl: result.sessionUrl });
      return;
    }

    setBusy(false);

    // A refusal is the server telling the player something worth hearing ("your subscription
    // already covers this"), so it gets a toast as well as the line in the dialog. A transport
    // failure does not: apiClient.js has already toasted that one.
    if (result.outcome === CHECKOUT_OUTCOME.REFUSED) {
      toast.error(result.message);
    }

    // The dialog is never closed on a failure - that is the WEB-P1-116 requirement - so the
    // Pay button below is its own retry.
    setError({ message: result.message, sessionUrl: null });
  };

  const handleCredit = async () => {
    setError(null);
    setBusy(true);

    const res = await RedeemAuctionPassCredit(row?.auctionId);

    setBusy(false);

    // Only `status: true` means the credit was applied. A refusal, a permission 401 (empty body)
    // or a gateway page carries no `true`, and used to be taken for success: the dialog closed
    // on an empty toast with the auction still locked.
    if (res?.data?.status !== true) {
      // No reply: apiClient.js has already said so.
      if (!res) return;

      // Kept open with the reason, as for Pay (WEB-P1-116).
      const message = readApiMessage(res);
      toast.error(message);
      setError({ message, sessionUrl: null });
      return;
    }

    toast.success(res.data.message);
    onClose();
    onRedeemed();
  };

  return (
    <ModalBox
      show={Boolean(row)}
      onHide={onClose}
      title={<h1 className="heading-dash">Unlock this auction</h1>}
      body={
        /* ModalBox wraps whatever it is given in a <p>, so these are divs rather than
           paragraphs - nesting a <p> inside one is invalid and React warns about it. */
        <>
          <div className="fw-bold mb-2">{row?.auctionName}</div>
          {/* The server owns this sentence, including the cases where the answer is
              "you do not need to buy anything". */}
          <div>{options?.message || "Checking..."}</div>
          {options?.canPurchase && (
            <div className="mt-2">
              This unlocks bidding on this auction only. Nothing renews, and no other auction is
              included.
            </div>
          )}
          {/* The payment page could not be reached. The dialog stays open and says so,
              instead of closing exactly as it would after a success. */}
          {error?.message && <div className="text-danger mt-2">{error.message}</div>}
          {error?.sessionUrl && (
            <div className="mt-2">
              If the payment page did not open,{" "}
              <a href={error.sessionUrl} rel="noopener noreferrer">
                open it here
              </a>
              .
            </div>
          )}
        </>
      }
      footer={
        <>
          {options?.canPurchase && (
            <Button disabled={busy} onClick={handlePurchase}>
              {busy ? "Opening checkout..." : `Pay $${options?.amount}`}
            </Button>
          )}
          {options?.canRedeemCredit && (
            <Button disabled={busy} onClick={handleCredit}>
              Apply credit
            </Button>
          )}
          <Button variant="secondary close-btn" onClick={onClose}>
            <CloseRounded />
          </Button>
        </>
      }
    />
  );
};
